// ==========================================================================
// OTP Verification Page - Mobile PWA
// ==========================================================================

import { backgroundImage, weatherIcon, poweredByAtram } from '@/assets';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useResendOtp, useVerifyOtp } from '@/hooks/useAuth';
import { userKeys } from '@/hooks/useUser';
import { config } from '@/lib/config';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

function maskPhone(phone: string): string {
  if (!phone) return '+***********';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 6) return phone;
  return `+${cleaned.slice(0, 3)}******${cleaned.slice(-2)}`;
}

export default function OTPVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const phone = (location.state as { phone?: string })?.phone;

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);

  const verifyOtpMutation = useVerifyOtp();
  const resendOtpMutation = useResendOtp();

  // Redirect back if no phone was passed (e.g. page refresh)
  useEffect(() => {
    if (!phone) {
      navigate('/auth/phone', { replace: true });
    }
  }, [phone, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Don't render anything while redirecting
  if (!phone) return null;

  const handleSubmit = async () => {

    if (otp.length !== 6) {
      setError(t('auth.otp.errorInvalidCode'));
      return;
    }

    setError('');
    // Mock mode for development
    if (config.enableMockApi) {
      navigate('/welcome');
      return;
    }

    try {
      await verifyOtpMutation.mutateAsync({ phone_number: phone, code: otp });
      // Invalidate user query so it refetches with the new token
      await queryClient.invalidateQueries({ queryKey: userKeys.all });
      navigate('/welcome');
    } catch {
      // Error shown via global toast - no local error needed
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || !phone) return;

    // Clear state
    setOtp('');
    setError('');

    // Mock mode for development
    if (config.enableMockApi) {
      setResendTimer(60);
      return;
    }

    try {
      await resendOtpMutation.mutateAsync({ phone_number: phone });
      setResendTimer(60);
    } catch {
      // All BE errors (including 429 rate limit) shown via global toast
    }
  };

  const isLoading = verifyOtpMutation.isPending;

  return (
    <div
      className="min-h-screen flex flex-col bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      {/* Top Text Section */}
      <div className="pt-12 px-6">
        <h1 className="font-display leading-[63px] tracking-[-3px] text-[55px]">
          <span className="font-bold text-black">{t('auth.phone.headline1')}</span>
        </h1>
        <div className="font-display font-light text-[50px] leading-[63px] tracking-[-3px] text-black">
          {t('auth.phone.headline2')}
        </div>
        <div className="font-display font-light text-[50px] leading-[63px] tracking-[-3px] text-black">
          {t('auth.phone.headline3')}
        </div>
      </div>

      {/* Center Weather Icon */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <img
          src={weatherIcon}
          alt="Weather"
          className="w-[140px] h-[140px] object-contain"
        />
      </div>

      {/* Bottom Form Section */}
      <div className="px-8 pb-4">
        <p className="text-[26px] font-display text-black mb-2">{t('auth.otp.title')}</p>
        <p className="text-black/60 font-display text-[14px] mb-5 leading-relaxed">
          {t('auth.otp.subtitle', { phone: maskPhone(phone || '') })}
        </p>

        {/* OTP Input */}
        <div className="mb-4">
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(value) => {
              setOtp(value);
              setError('');
            }}
          >
            <InputOTPGroup className="gap-2 w-full">
              <InputOTPSlot
                index={0}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
              <InputOTPSlot
                index={1}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
              <InputOTPSlot
                index={2}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
              <InputOTPSlot
                index={3}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
              <InputOTPSlot
                index={4}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
              <InputOTPSlot
                index={5}
                className="flex-1 h-[54px] !rounded-xl !border-0 bg-white text-[20px] font-semibold"
              />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {error && <p className="text-red-500 text-[14px] mb-3 text-center">{error}</p>}

        {/* Continue Button */}
        <button
          onClick={handleSubmit}
          disabled={isLoading || otp.length !== 6}
          className="w-full h-[54px] bg-brand-primary text-white text-[14px] font-semibold tracking-wider rounded-2xl disabled:opacity-60 mb-4"
        >
          {isLoading ? t('common.verifying') : t('common.continue')}
        </button>

        {/* Resend */}
        <button
          onClick={handleResend}
          disabled={resendTimer > 0 || resendOtpMutation.isPending}
          className="w-full text-black/50 text-[15px] font-medium py-2 disabled:opacity-50 mb-4"
        >
          {resendTimer > 0
            ? t('auth.otp.resendOtpIn', { seconds: resendTimer })
            : t('auth.otp.resendOtp')}
        </button>

        {/* Powered by Atram */}
        <div className="flex items-center justify-center">
          <img
            src={poweredByAtram}
            alt="Powered by Atram"
            className="h-[30px] object-contain"
          />
        </div>
      </div>
    </div>
  );
}
